import axios from "axios";
import { getData } from "../contexts/authContext";
import { capitalize } from "utils/formatters";

export const getCoachFreeformAthletes = async (
  clinicianId: string,
  searchValue?: string,
  sort?: string
) => {
  const token = await getData("idToken");
  let url = `${process.env.REACT_APP_BASE_URL}/clinician/${clinicianId}/freeform-athletes`;

  if (searchValue) {
    url = `${url}?name=${searchValue}`;
  }

  if (sort) {
    const symbol = searchValue ? "&" : "?";
    url = `${url}${symbol}sort=${sort}`;
  }
  const { data } = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return data;
};

// Define the interface for the payload
interface ClinicianPayload {
  firstName: string;
  lastName: string;
  email: string;
  clinicId: string;
  role: string[];
}

export const createClinicianPromise = async (payload: ClinicianPayload) => {
  const firstNameCapitalized = capitalize(payload.firstName);
  const lastNameCapitalized = capitalize(payload.lastName);
  const parsedPayload = {
    ...payload,
    firstName: firstNameCapitalized,
    lastName: lastNameCapitalized,
  };
  const token = await getData("idToken");
  const url = `${process.env.REACT_APP_BASE_URL}/clinician`;

  const { data } = await axios.post(url, parsedPayload, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return data;
};

export const createExamVideoPresignedUrl = async (payload: {
  patientId: string;
  examId: string;
  fileExtension: string;
  contentType: string;
}) => {
  const token = await getData("idToken");
  const url = `${process.env.REACT_APP_BASE_URL}/exam-video/create-presign-url`;

  const { data } = await axios.post(url, payload, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return data;
};

export const updateClinicianRecommendations = async (
  clinicianId: string,
  payload: {
    recommendations: {
      normal?: string;
      significant?: string;
      severe?: string;
      normalLink?: string;
      significantLink?: string;
      severeLink?: string;
      normalLinkText?: string;
      significantLinkText?: string;
      severeLinkText?: string;
    };
  }
) => {
  const token = await getData("idToken");
  const url = `${process.env.REACT_APP_BASE_URL}/clinician/${clinicianId}`;
  const { data } = await axios.patch(url, payload, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return data;
};

export const createStripeCheckoutSession = async (priceId: string) => {
  const token = await getData("idToken");
  const url = `${process.env.REACT_APP_BASE_URL}/payment/create-checkout-session`;

  const { data } = await axios.post(
    url,
    { priceId },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return data;
};

export const getBillingPortalUrl = async () => {
  const token = await getData("idToken");
  const url = `${process.env.REACT_APP_BASE_URL}/clinician/billing-portal`;
  const { data } = await axios.get(url, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data;
};

export const cancelSubscription = async () => {
  const token = await getData("idToken");
  const url = `${process.env.REACT_APP_BASE_URL}/subscription/cancel`;
  const { data } = await axios.post(
    url,
    {},
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return data;
};

export const getPaymentHistory = async () => {
  const token = await getData("idToken");
  const url = `${process.env.REACT_APP_BASE_URL}/payment/history`;

  const { data } = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return data;
};

export const conciergeRequest = async () => {
  const token = await getData("idToken");
  const url = `${process.env.REACT_APP_BASE_URL}/clinician/trigger-email`;

  const { data } = await axios.post(
    url,
    {
      template: "CONTACT_US",
    },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return data;
};

export const updateLifetimeAccess = async (value: boolean) => {
  const token = await getData("idToken");
  const url = `${process.env.REACT_APP_BASE_URL}/clinician/update-lifetime-access`;
  const { data } = await axios.put(
    url,
    {
      hasLifetimeAccess: value,
    },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return data;
};
